import React from 'react';
import { Alert, Button, Box } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { setSelectedDepartement } from '../reduxStore/AppSlice';

/**
 * Wrapper symétrique de `DeptRequiredRoute`.
 * Bloque l'accès aux routes tenant-level (gestion globale : départements,
 * utilisateurs, rôles, droits, options, templates rapports) quand
 * l'utilisateur est dans un contexte département.
 *
 * En mode ALL : laisse passer le children.
 * En mode mono-département (un seul département autorisé, cf.
 * useDepartementBootstrap) : laisse passer aussi — "ce département" et
 * "Tous les départements" représentent exactement les mêmes données quand il
 * n'y en a qu'un ; sans cette exception /departement (le seul moyen d'en
 * ajouter un) resterait injoignable pour un tenant mono-département.
 * En mode dept (2+ départements) : alerte + bouton qui reset le contexte →
 * DepartementSelectionPage, d'où l'utilisateur peut choisir "Tous les
 * départements" (si sa blacklist le permet) ou repartir vers un dept normal.
 */
const AllModeRequiredRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAllMode } = useSelector((s: RootState) => s.AppSlice);
  const allowedDepartements = useSelector((s: RootState) => s.ScopeSlice.allowedDepartements);
  const isMonoDepartement = allowedDepartements.length === 1;

  if (isAllMode || isMonoDepartement) return <>{children}</>;

  return (
    <Box sx={{ p: 3 }}>
      <Alert
        severity="info"
        icon={<PublicIcon />}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => dispatch(setSelectedDepartement(null))}
          >
            Changer de contexte
          </Button>
        }
      >
        Cette page est accessible uniquement en mode "Tous départements".
      </Alert>
    </Box>
  );
};

export default AllModeRequiredRoute;
